'use client';

import {
  Card,
  ClientDashboardMobileSegments,
  Flex,
  Heading,
  QuestionnaireCard,
  LayoutContent,
  Select,
  Shimmer,
  Text,
} from '@/_components';
import { LoadingCard } from './components/LoadingCard';
import { QuestionnaireFirmAside } from './components/QuestionnaireFirmAside';
import { useContext, useMemo, useCallback, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ClientTaxReturnsContext } from '@/_contexts/client/ClientTaxReturnsProvider';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import styles from './ClientDashboard.module.css';
import type { ClientDashboardProps } from './ClientDashboard.types';
import {
  mapQuestionnaireSection,
  mapQuestionnaireStatus,
  getTriggeredSections,
} from '@/_utils/sections';
import { TaxReturnDeliveryCard } from './components/TaxReturnDeliveryCard';
import { EnhancedTaxReturnDeliveryCard } from './components/EnhancedTaxReturnDeliveryCard';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ClientDashboardContext } from '@/_contexts/client/ClientDashboardProvider';
import { EmptyDashboard } from './components/EmptyDashboard';
import { InlineEntitySelector } from './components/InlineEntitySelector';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { MyTasks } from './components/MyTasks';
import { EntityYearSelector } from './components/EntityYearSelector';
import { SelectedEntityDisplay } from './components/SelectedEntityDisplay';
import { CompletedItemsSection } from './components/CompletedItemsSection';
import { EnhancedQuestionnaireCard } from './components/EnhancedQuestionnaireCard';
import { SectionBar } from './components/SectionBar';
import { AllDoneDashboard } from './components/AllDoneDashboard';
import type { Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import { TaxReturn } from '@/_types/models/taxReturns';
import { useSearchParams } from 'next/navigation';
import { getExpiredDaysAgo } from '@/_utils/dates';

export const ClientDashboard: FC<ClientDashboardProps> = ({ isPreview }) => {
  const searchParams = useSearchParams();
  const k1DistributionId = searchParams.get('k1-distribution-id') || undefined;

  const { preferences, firm } = useContext(FirmSettingsContext);
  const { filteredTaxReturns, isClientLoading, _getTaxReturns } = useContext(
    ClientTaxReturnsContext,
  );
  const { filteredQuestionnaires, isLoading: isQuestionnairesLoading } =
    useContext(ClientQuestionnaireContext);
  const { user, fullName, selectedCompanyId } = useContext(UserContext);
  const { setYearFilter, yearFilter, years } = useContext(
    ClientDashboardContext,
  );
  const {
    previewFullName,
    isPreviewLoading,
    previewQuestionnaires,
    previewUserTaxReturns,
    previewYearFilter,
    setPreviewYearFilter,
    previewUserCompanyId,
  } = useContext(UserPreviewContext);

  const dashboardQuestionnaires: Array<Questionnaire> = useMemo(
    () => (isPreview ? previewQuestionnaires : filteredQuestionnaires),
    [isPreview, previewQuestionnaires, filteredQuestionnaires],
  );

  const dashboardName: string | undefined = useMemo(
    () => (isPreview ? previewFullName : fullName),
    [isPreview, previewFullName, fullName],
  );

  const taxReturns: Array<TaxReturn> = useMemo(() => {
    return isPreview ? previewUserTaxReturns : filteredTaxReturns;
  }, [isPreview, previewUserTaxReturns, filteredTaxReturns]);

  const hasQuestionnaires: boolean = !!dashboardQuestionnaires?.length;

  const onSelect = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;

    return isPreview ? setPreviewYearFilter(newValue) : setYearFilter(newValue);
  };

  const realYearFilter: string = useMemo(
    () => (isPreview ? previewYearFilter : yearFilter),
    [isPreview, previewYearFilter, yearFilter],
  );

  const hasTaxReturns: boolean = Boolean(
    preferences?.new_tax_return_delivery_enabled && !!taxReturns?.length,
  );

  const areItemsLoading: boolean = isPreview
    ? isPreviewLoading
    : isQuestionnairesLoading || isClientLoading;

  const areItemsEmpty: boolean =
    !areItemsLoading && !hasTaxReturns && !hasQuestionnaires;

  const clientDashboardTitle: string = areItemsLoading
    ? ''
    : hasTaxReturns && hasQuestionnaires
      ? 'Questionnaires & Tax Returns'
      : hasTaxReturns
        ? 'Tax Returns'
        : 'Questionnaires';

  const isNewDashboardEnabled: boolean =
    !!preferences?.tmp_new_client_dashboard;

  // Priority calculation functions
  const getQuestionnairePriority = useCallback(
    (questionnaire: Questionnaire): 'urgent' | 'caution' | 'normal' => {
      const dayDifference = getExpiredDaysAgo(questionnaire.due_date);
      const dayDifferenceCount = Math.abs(
        getExpiredDaysAgo(questionnaire.due_date),
      );

      // Urgent: overdue or due today
      if (dayDifference > 0 || dayDifferenceCount <= 1) {
        return 'urgent';
      }
      // Caution: due within 7 days
      if (dayDifferenceCount <= 7) {
        return 'caution';
      }
      return 'normal';
    },
    [],
  );

  const getTaxReturnPriority = useCallback(
    (taxReturn: TaxReturn): 'urgent' | 'caution' | 'normal' => {
      // Urgent if both signatures and payments are pending
      if (!taxReturn.all_signed && taxReturn.remaining_payment_count > 0) {
        return 'urgent';
      }
      // Caution if only one type is pending
      if (!taxReturn.all_signed || taxReturn.remaining_payment_count > 0) {
        return 'caution';
      }
      return 'normal';
    },
    [],
  );

  // Separate completed and in-progress items for new dashboard
  const {
    completedQuestionnaires,
    completedTaxReturns,
    sortedInProgressItems,
  } = useMemo(() => {
    if (!isNewDashboardEnabled) {
      return {
        inProgressQuestionnaires: dashboardQuestionnaires,
        completedQuestionnaires: [],
        inProgressTaxReturns: [],
        completedTaxReturns: [],
        sortedInProgressItems: [],
      };
    }

    // Filter by selected entity for new dashboard
    const currentCompanyId = isPreview
      ? previewUserCompanyId
      : selectedCompanyId;

    const filteredQuestionnaires = dashboardQuestionnaires.filter(
      (q) => q.company_id === currentCompanyId,
    );

    const filteredTaxReturns =
      taxReturns?.filter((tr) => tr.company_id === currentCompanyId) || [];

    const inProgress = filteredQuestionnaires.filter((q) => {
      const mappedStatus = mapQuestionnaireStatus(q);
      return mappedStatus !== 'completed';
    });

    const completed = filteredQuestionnaires.filter((q) => {
      const mappedStatus = mapQuestionnaireStatus(q);
      return mappedStatus === 'completed';
    });

    // Separate tax returns based on completion status
    const inProgressTaxReturns = filteredTaxReturns.filter((tr) => {
      return !tr.all_signed || !tr.all_paid;
    });

    const completedTaxReturns = filteredTaxReturns.filter((tr) => {
      return tr.all_signed && tr.all_paid;
    });

    // Create combined sorted list for in-progress items
    const combinedInProgressItems = [
      ...inProgress.map((q) => ({
        type: 'questionnaire' as const,
        item: q,
        priority: getQuestionnairePriority(q),
      })),
      ...inProgressTaxReturns.map((tr) => ({
        type: 'tax_return' as const,
        item: tr,
        priority: getTaxReturnPriority(tr),
      })),
    ].sort((a, b) => {
      const priorityOrder = { urgent: 0, caution: 1, normal: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    return {
      inProgressQuestionnaires: inProgress,
      completedQuestionnaires: completed,
      inProgressTaxReturns,
      completedTaxReturns,
      sortedInProgressItems: combinedInProgressItems,
    };
  }, [
    dashboardQuestionnaires,
    taxReturns,
    isNewDashboardEnabled,
    getQuestionnairePriority,
    getTaxReturnPriority,
    selectedCompanyId,
    isPreview,
    previewUserCompanyId,
  ]);

  if (!user) return null;

  const hasCompletedSection: boolean =
    completedQuestionnaires.length > 0 || completedTaxReturns.length > 0;

  if (isNewDashboardEnabled) {
    return (
      <LayoutContent>
        <Flex isColumn={true} gap={4}>
          {dashboardName ? (
            <Text type="small" color="disabled">
              Welcome, {dashboardName}!
            </Text>
          ) : (
            <Flex isYCentered={true} gap={8}>
              <Shimmer
                width={120}
                height={36}
                paddingBottom={4}
                paddingTop={4}
              />
              <Shimmer
                width={180}
                height={36}
                paddingBottom={4}
                paddingTop={4}
              />
            </Flex>
          )}
          <SelectedEntityDisplay isPreview={isPreview} />
        </Flex>
        <ClientDashboardMobileSegments />
        <Card>
          <Flex padding={12} gap={6} className={styles.clientDashboard}>
            <Flex
              isColumn={true}
              gap={10}
              className={styles.clientDashboardContainer}
            >
              <Flex
                isBetween={true}
                isYCentered={true}
                isWrapping={true}
                gap={4}
              >
                <Heading level={4}>{clientDashboardTitle}</Heading>
                <EntityYearSelector isPreview={isPreview} />
              </Flex>
              <Flex
                isColumn={true}
                gap={10}
                data-testid="ClientDashboard-questionnaires-section"
              >
                {areItemsLoading && <LoadingCard />}
                {areItemsEmpty && <EmptyDashboard />}

                {/* In Progress Section */}
                {sortedInProgressItems.length > 0 && (
                  <>
                    <SectionBar
                      title="In Progress"
                      count={sortedInProgressItems.length}
                      color="info"
                    />
                    {sortedInProgressItems.map((item, key) => {
                      if (item.type === 'tax_return') {
                        return (
                          <EnhancedTaxReturnDeliveryCard
                            key={`tax-${key}`}
                            taxReturn={item.item}
                            onTaxReturnUpdate={_getTaxReturns}
                            k1DistributionId={k1DistributionId}
                          />
                        );
                      } else {
                        const questionnaire = item.item;
                        const previewableSections: Array<Section> =
                          getTriggeredSections(
                            questionnaire?.sections,
                            questionnaire?.questionnaire_questions,
                            true,
                          );

                        const mappedSections =
                          previewableSections?.map((s) =>
                            mapQuestionnaireSection(s),
                          ) || [];

                        const questionnaireStatus =
                          mapQuestionnaireStatus(questionnaire);

                        const selectedFirmStatus =
                          typeof questionnaire?.firm_custom_status_id ===
                          'number'
                            ? firm?.firm_custom_statuses?.find(
                                (status) =>
                                  status.id ===
                                  questionnaire?.firm_custom_status_id,
                              )
                            : undefined;

                        const cardDescription: string = questionnaire.locked
                          ? `${questionnaire.name} is locked from further edits, you can only view your answers. If you have any questions, please send us a message.`
                          : selectedFirmStatus
                            ? selectedFirmStatus.client_instruction
                            : 'Please complete all questions.';

                        return (
                          <EnhancedQuestionnaireCard
                            key={`questionnaire-${key}`}
                            status={questionnaireStatus}
                            customStatus={
                              selectedFirmStatus?.client_display_status
                            }
                            dueDate={questionnaire.due_date}
                            title={questionnaire.formatted_name}
                            isPreview={isPreview}
                            sections={mappedSections}
                            description={cardDescription}
                            questionnaire={questionnaire}
                          />
                        );
                      }
                    })}
                  </>
                )}

                {/* Completed Section */}
                {hasCompletedSection && (
                  <CompletedItemsSection
                    completedCount={
                      completedQuestionnaires.length +
                      completedTaxReturns.length
                    }
                  >
                    {completedQuestionnaires.map((questionnaire, key) => {
                      const previewableSections: Array<Section> =
                        getTriggeredSections(
                          questionnaire?.sections,
                          questionnaire?.questionnaire_questions,
                          true,
                        );

                      const mappedSections =
                        previewableSections?.map((s) =>
                          mapQuestionnaireSection(s),
                        ) || [];

                      const questionnaireStatus =
                        mapQuestionnaireStatus(questionnaire);

                      const selectedFirmStatus =
                        typeof questionnaire?.firm_custom_status_id === 'number'
                          ? firm?.firm_custom_statuses?.find(
                              (status) =>
                                status.id ===
                                questionnaire?.firm_custom_status_id,
                            )
                          : undefined;

                      const cardDescription: string = questionnaire.locked
                        ? `${questionnaire.name} is locked from further edits, you can only view your answers. If you have any questions, please send us a message.`
                        : selectedFirmStatus
                          ? selectedFirmStatus.client_instruction
                          : 'Please complete all questions.';

                      return (
                        <EnhancedQuestionnaireCard
                          key={key}
                          status={questionnaireStatus}
                          customStatus={
                            selectedFirmStatus?.client_display_status
                          }
                          dueDate={questionnaire.due_date}
                          title={questionnaire.formatted_name}
                          isPreview={isPreview}
                          sections={mappedSections}
                          description={cardDescription}
                          questionnaire={questionnaire}
                        />
                      );
                    })}
                    {preferences?.new_tax_return_delivery_enabled &&
                      completedTaxReturns.map((taxReturn, key) => {
                        return (
                          <EnhancedTaxReturnDeliveryCard
                            key={`completed-${key}`}
                            taxReturn={taxReturn}
                            onTaxReturnUpdate={_getTaxReturns}
                            k1DistributionId={k1DistributionId}
                          />
                        );
                      })}
                  </CompletedItemsSection>
                )}
                {!areItemsLoading &&
                  !areItemsEmpty &&
                  sortedInProgressItems.length === 0 &&
                  (completedQuestionnaires.length > 0 ||
                    completedTaxReturns.length > 0) && (
                    <AllDoneDashboard
                      isPreview={isPreview}
                      selectedYear={realYearFilter.toString()}
                    />
                  )}
              </Flex>
            </Flex>
            <Flex isColumn={true} gap={8}>
              <MyTasks isPreview={isPreview} />
              <QuestionnaireFirmAside isPreview={isPreview} />
            </Flex>
          </Flex>
        </Card>
      </LayoutContent>
    );
  }

  return (
    <LayoutContent>
      <Flex isColumn={true} gap={1}>
        {dashboardName ? (
          <Heading level={1} data-testid="ClientDashboard-welcome-header">
            Welcome, {dashboardName}!
          </Heading>
        ) : (
          <Flex isYCentered={true} gap={8}>
            <Shimmer width={120} height={36} paddingBottom={4} paddingTop={4} />
            <Shimmer width={180} height={36} paddingBottom={4} paddingTop={4} />
          </Flex>
        )}
        <InlineEntitySelector />
      </Flex>
      <ClientDashboardMobileSegments />
      <Card>
        <Flex padding={12} gap={6} className={styles.clientDashboard}>
          <Flex
            isColumn={true}
            gap={10}
            className={styles.clientDashboardContainer}
          >
            <Flex isBetween={true} isYCentered={true} isWrapping={true} gap={4}>
              <Heading level={4}>{clientDashboardTitle}</Heading>
              <Flex gap={4} isYCentered={true}>
                <Heading level={6} isSameLine={true}>
                  Tax Year:
                </Heading>
                <Select
                  label="Tax Year"
                  isDisabled={!years?.length}
                  isLabelHidden={true}
                  onChange={onSelect}
                  selectedValue={realYearFilter}
                  items={years}
                />
              </Flex>
            </Flex>
            <Flex
              isColumn={true}
              gap={10}
              data-testid="ClientDashboard-questionnaires-section"
            >
              {areItemsLoading && <LoadingCard />}
              {areItemsEmpty && <EmptyDashboard />}
              {preferences?.new_tax_return_delivery_enabled &&
                taxReturns?.map((taxReturn, key) => {
                  return (
                    <TaxReturnDeliveryCard
                      key={key}
                      taxReturn={taxReturn}
                      onTaxReturnUpdate={_getTaxReturns}
                      k1DistributionId={k1DistributionId}
                    />
                  );
                })}
              {dashboardQuestionnaires.map((questionnaire, key) => {
                const previewableSections: Array<Section> =
                  getTriggeredSections(
                    questionnaire?.sections,
                    questionnaire?.questionnaire_questions,
                    true,
                  );

                const mappedSections =
                  previewableSections?.map((s) => mapQuestionnaireSection(s)) ||
                  [];

                const questionnaireStatus =
                  mapQuestionnaireStatus(questionnaire);

                const selectedFirmStatus =
                  typeof questionnaire?.firm_custom_status_id === 'number'
                    ? firm?.firm_custom_statuses?.find(
                        (status) =>
                          status.id === questionnaire?.firm_custom_status_id,
                      )
                    : undefined;

                const cardDescription: string = questionnaire.locked
                  ? `${questionnaire.name} is locked from further edits, you can only view your answers. If you have any questions, please send us a message.`
                  : selectedFirmStatus
                    ? selectedFirmStatus.client_instruction
                    : 'Please complete all questions.';

                return (
                  <QuestionnaireCard
                    key={key}
                    status={questionnaireStatus}
                    customStatus={selectedFirmStatus?.client_display_status}
                    dueDate={questionnaire.due_date}
                    title={questionnaire.formatted_name}
                    isPreview={isPreview}
                    sections={mappedSections}
                    description={cardDescription}
                    questionnaire={questionnaire}
                  />
                );
              })}
            </Flex>
          </Flex>
          <QuestionnaireFirmAside isPreview={isPreview} />
        </Flex>
      </Card>
    </LayoutContent>
  );
};
